import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Trash2, Eye, Link2, Lock, Unlock, ShieldCheck, FileText } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import api, { formatApiErrorDetail } from "../../lib/api";

export const onboardUrl = (token) => `${window.location.origin}/onboard/${token}`;
const fail = (t) => (e) => toast.error(t, { description: formatApiErrorDetail(e.response?.data?.detail) });

export function CopyLink({ token, testid }) {
  const [done, setDone] = useState(false);
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(onboardUrl(token)).then(() => { setDone(true); toast.success("Onboarding link copied"); setTimeout(() => setDone(false), 1500); });
  };
  return (
    <button onClick={copy} data-testid={testid} className="inline-flex items-center gap-1.5 text-xs border border-brand-border rounded-full px-3 py-1.5 text-brand-ink hover:bg-brand-hover transition-colors">
      {done ? <Check className="h-3.5 w-3.5 text-emerald-700" /> : <Copy className="h-3.5 w-3.5" />} Copy link
    </button>
  );
}

function IdViewer({ m }) {
  const [url, setUrl] = useState(null);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);
  const load = () => api.get(`/onboarding/members/${m.id}/id-file`, { responseType: "blob" })
    .then(({ data }) => setUrl(URL.createObjectURL(data))).catch(fail("Could not load ID"));
  if (!m.has_id) return <span className="text-xs text-brand-tertiary">ID deleted</span>;
  if (!url) return <button onClick={load} data-testid={`onb-view-id-${m.id}`} className="inline-flex items-center gap-1 text-xs text-brand-primary hover:underline"><Eye className="h-3.5 w-3.5" /> View ID</button>;
  return m.id_mime?.startsWith("image/")
    ? <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="ID" data-testid={`onb-id-img-${m.id}`} className="mt-2 max-h-56 rounded border border-brand-border" /></a>
    : <a href={url} target="_blank" rel="noreferrer" data-testid={`onb-id-pdf-${m.id}`} className="inline-flex items-center gap-1 text-xs text-brand-primary hover:underline"><FileText className="h-3.5 w-3.5" /> Open {m.id_filename}</a>;
}

function MemberRow({ m, onChange }) {
  const act = (fn, ok) => fn().then(() => { toast.success(ok); onChange(); }).catch(fail("Action failed"));
  return (
    <div className="border border-brand-border rounded-lg px-4 py-3 bg-brand-surface" data-testid={`onb-member-${m.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[180px]">
          <p className="text-sm text-brand-ink font-medium">{m.first_name} {m.last_name}</p>
          <p className="text-xs text-brand-muted">{m.email} · {new Date(m.submitted_at).toLocaleDateString()}</p>
        </div>
        <button onClick={() => act(() => api.post(`/onboarding/members/${m.id}/approve`), m.status === "approved" ? "Marked pending" : "Approved")} data-testid={`onb-approve-${m.id}`}
          className={`inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 transition-colors ${m.status === "approved" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-brand-ink text-brand-page hover:opacity-90"}`}>
          <ShieldCheck className="h-3.5 w-3.5" /> {m.status === "approved" ? "Approved" : "Approve"}
        </button>
        {m.has_id && <button onClick={() => window.confirm("Delete this ID copy? Name and email are kept.") && act(() => api.delete(`/onboarding/members/${m.id}/id-file`), "ID deleted")} data-testid={`onb-delete-id-${m.id}`} className="text-xs text-brand-muted hover:text-rose-700 transition-colors">Delete ID</button>}
        <button onClick={() => window.confirm(`Remove ${m.first_name}?`) && act(() => api.delete(`/onboarding/members/${m.id}`), "Removed")} data-testid={`onb-delete-member-${m.id}`} className="text-brand-muted hover:text-rose-700 transition-colors"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="mt-1"><IdViewer m={m} /></div>
    </div>
  );
}

export function CompanyDrawer({ id, onClose, onChanged }) {
  const [c, setC] = useState(null);
  const load = () => api.get(`/onboarding/${id}`).then(({ data }) => setC(data))
    .catch((e) => { fail("Could not open this onboarding")(e); onClose(); });
  useEffect(() => { if (id) { setC(null); load(); } }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => { load(); onChanged(); };
  const patch = (body, ok) => api.patch(`/onboarding/${id}`, body).then(({ data }) => { setC(data); onChanged(); toast.success(ok); }).catch(fail("Update failed"));
  const newLink = () => window.confirm("Create a new link? The old link stops working.") &&
    api.post(`/onboarding/${id}/new-link`).then(({ data }) => { setC(data); onChanged(); toast.success("New link created"); }).catch(fail("Failed"));
  const remove = () => window.confirm("Delete this onboarding and all submitted people and IDs?") &&
    api.delete(`/onboarding/${id}`).then(() => { onChanged(); onClose(); }).catch(fail("Delete failed"));
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[640px] bg-brand-page border-brand-border overflow-y-auto" data-testid="onb-drawer">
        <SheetHeader className="text-left">
          <SheetTitle className="font-serif text-2xl font-light text-brand-ink">{c?.company_name || "Onboarding"}</SheetTitle>
          <SheetDescription className="text-xs text-brand-muted">{c && `${c.location}${c.start_date ? ` · starts ${c.start_date}` : ""}${c.contact_email ? ` · ${c.contact_name || ""} ${c.contact_email}` : ""}`}</SheetDescription>
        </SheetHeader>
        {c && (
          <div className="mt-6 space-y-6">
            <div className="border border-brand-border rounded-lg p-4 bg-brand-surface space-y-3">
              <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">Onboarding link — paste into the Yardi welcome email</p>
              <p className="font-mono text-xs text-brand-ink break-all" data-testid="onb-link-url">{onboardUrl(c.token)}</p>
              <div className="flex flex-wrap gap-2">
                <CopyLink token={c.token} testid="onb-drawer-copy" />
                <button onClick={newLink} data-testid="onb-new-link" className="inline-flex items-center gap-1.5 text-xs border border-brand-border rounded-full px-3 py-1.5 text-brand-ink hover:bg-brand-hover transition-colors"><Link2 className="h-3.5 w-3.5" /> New link</button>
                <button onClick={() => patch({ status: c.status === "open" ? "closed" : "open" }, c.status === "open" ? "Link closed" : "Link reopened")} data-testid="onb-toggle-status"
                  className="inline-flex items-center gap-1.5 text-xs border border-brand-border rounded-full px-3 py-1.5 text-brand-ink hover:bg-brand-hover transition-colors">
                  {c.status === "open" ? <><Lock className="h-3.5 w-3.5" /> Close link</> : <><Unlock className="h-3.5 w-3.5" /> Reopen link</>}
                </button>
                <button onClick={remove} data-testid="onb-delete-company" className="ml-auto text-xs text-brand-muted hover:text-rose-700 transition-colors">Delete</button>
              </div>
              {c.status === "closed" && <p className="text-xs text-amber-800" data-testid="onb-closed-note">This link is closed. Members see a "link closed" message.</p>}
            </div>
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">Team submitted ({c.members.length})</p>
              {!c.members.length && <p className="text-sm text-brand-muted" data-testid="onb-no-members">No one has submitted yet.</p>}
              {c.members.map((m) => <MemberRow key={m.id} m={m} onChange={refresh} />)}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
